import React, { useEffect, useState } from "react";
import { Download, Share, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "hiti_install_dismissed_at";
const DISMISS_DAYS = 7;

function isiOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  const t = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
  if (!t) return false;
  return Date.now() - t < DISMISS_DAYS * 86400 * 1000;
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosGuide, setShowIosGuide] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    // Android / Chrome flow
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS flow — Safari never fires beforeinstallprompt, so show our guide after a delay
    let iosTimer;
    if (isiOS()) {
      iosTimer = setTimeout(() => setVisible(true), 6000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIosGuide(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") dismiss();
      else dismiss();
      setDeferred(null);
    } else if (isiOS()) {
      setShowIosGuide(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Bottom banner */}
      <div
        data-testid="install-prompt"
        className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm bg-halo-navy text-halo-cream border-2 border-halo-red shadow-2xl"
      >
        <div className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 bg-halo-red flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-sports uppercase tracking-tight text-lg leading-tight">Install HITI on your phone</h3>
            <p className="font-body text-xs text-halo-cream/70 mt-1">
              {isiOS()
                ? "Get faster access — add to your home screen."
                : "One tap to install. Works offline-friendly, full-screen."}
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="install-btn"
                onClick={install}
                className="bg-halo-red hover:bg-halo-redHover text-white font-sports uppercase tracking-wider px-3 py-2 text-xs inline-flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={2.5} />
                {isiOS() ? "Show How" : "Install"}
              </button>
              <button
                data-testid="install-dismiss"
                onClick={dismiss}
                className="font-sports uppercase tracking-widest text-[10px] text-halo-cream/60 hover:text-halo-cream px-2 py-2"
              >
                Not now
              </button>
            </div>
          </div>
          <button onClick={dismiss} aria-label="Close" className="text-halo-cream/50 hover:text-halo-cream">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* iOS instructions modal */}
      {showIosGuide && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={dismiss}>
          <div
            data-testid="ios-install-guide"
            onClick={(e) => e.stopPropagation()}
            className="bg-halo-cream max-w-sm w-full border-4 border-halo-navy"
          >
            <div className="bg-halo-navy text-halo-cream p-4 flex items-center justify-between">
              <h3 className="font-sports uppercase tracking-tight text-xl">Install on iPhone</h3>
              <button onClick={dismiss} aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <Step n="1">
                Tap the <Share className="inline w-4 h-4 mx-1 text-halo-red" strokeWidth={2.5} />
                <strong>Share</strong> icon at the bottom of Safari.
              </Step>
              <Step n="2">
                Scroll down and tap <strong>"Add to Home Screen"</strong>.
              </Step>
              <Step n="3">
                Tap <strong>Add</strong> in the top-right. HITI lives on your home screen now.
              </Step>
              <p className="text-xs text-halo-navy/60 font-body pt-3 border-t border-halo-navy/20">
                Note: Must be opened in Safari (not Chrome) on iPhone.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const Step = ({ n, children }) => (
  <div className="flex gap-3">
    <div className="w-7 h-7 bg-halo-red text-white font-sports flex items-center justify-center shrink-0">{n}</div>
    <p className="font-body text-sm text-halo-navy leading-relaxed">{children}</p>
  </div>
);
